import React, { useEffect, useState } from "react";
import { Badge, Divider, Group, Modal, NumberInput, SimpleGrid, Skeleton, Stack, Text, TextInput, Title } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { Alert, Button, Card, ConfirmModal, spacePx } from "@xplatform/design-system";
import { IconTrash } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { CatalogModel, ModelPrice, PriceEntry } from "@models/providerCatalog";
import { providerCatalogApi } from "@services/providerCatalogApi";
import { errorMessage } from "@hooks/monitoring/useApiResource";
import { AdminTable, col } from "@components/AdminTable";
import { ALL_RATES, rateText, ratesFor, unitsText, usesUnits, type RateField } from "./priceFormat";

/** A QuantityUnits unit: snake_case, never one with a rate of its own. */
const UNIT = /^[a-z][a-z0-9_]{0,39}$/;
const COUNTED = new Set(["tokens", "pages", "images"]);

interface PriceModalProps {
  model: CatalogModel | null;
  providerLabel?: string;
  canManage: boolean;
  onClose: () => void;
  /** Called after any change, so the catalog can reload its prices. */
  onChanged: () => void;
}

const SOURCE_COLOR = { admin: "blue", library: "green", none: "orange" } as const;

/**
 * The price of one model: what it is charged at now (admin entry, else the library
 * rates, else nothing) and, for admins, the admin price history and a form to add
 * an entry. An admin price wins over the library price; it applies to calls made
 * after the runtime's price cache refreshes and never re-prices recorded usage.
 */
const PriceModal: React.FC<PriceModalProps> = ({ model, providerLabel, canManage, onClose, onChanged }) => {
  const { t } = useTranslation();
  const [history, setHistory] = useState<ModelPrice | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [from, setFrom] = useState<Date | null>(new Date());
  const [rates, setRates] = useState<Partial<Record<RateField, number | string>>>({});
  const [unit, setUnit] = useState("");
  const [unitRate, setUnitRate] = useState<number | string>("");

  useEffect(() => {
    if (!model) return;
    const controller = new AbortController();
    setHistory(null);
    setError(null);
    setRates({});
    setUnit("");
    setUnitRate("");
    setFrom(new Date());
    setLoading(true);
    providerCatalogApi().price(model.provider_key, model.model_id, controller.signal)
      .then((p) => !controller.signal.aborted && setHistory(p))
      .catch((err) => !controller.signal.aborted && setError(errorMessage(err)))
      .finally(() => !controller.signal.aborted && setLoading(false));
    return () => controller.abort();
  }, [model]);

  if (!model) return null;
  const fields = ratesFor(model.capability);
  const filled = fields.filter((f) => rates[f] !== undefined && rates[f] !== "");
  const perUnit = usesUnits(model.capability);
  const unitInvalid = unit.trim() !== "" && (!UNIT.test(unit.trim()) || COUNTED.has(unit.trim()));
  const unitFilled = perUnit && unit.trim() !== "" && !unitInvalid && unitRate !== "";

  const change = async (work: () => Promise<ModelPrice | null>) => {
    setSaving(true);
    setError(null);
    try {
      setHistory(await work());
      setRates({});
      setUnitRate("");
      onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  const addEntry = () => change(() => {
    const entry: PriceEntry = { effective_from: dayjs(from).format("YYYY-MM-DD") };
    for (const field of filled) entry[field] = Number(rates[field]);
    if (unitFilled) entry.units = { [unit.trim()]: Number(unitRate) };
    return providerCatalogApi().addPriceEntry(model.provider_key, model.model_id, entry);
  });
  const removeEntry = (entry: PriceEntry) => change(async () => {
    const rest = (history?.prices ?? []).filter((e) => e.effective_from !== entry.effective_from);
    if (!rest.length) {
      await providerCatalogApi().deletePrice(model.provider_key, model.model_id);
      return null;
    }
    return providerCatalogApi().replacePrice(model.provider_key, model.model_id, { note: history?.note, prices: rest });
  });

  // What the model is charged at now, per rate the capability uses.
  const current: Partial<Record<RateField, number | null>> = model.admin_price
    ? (model.admin_price as Partial<Record<RateField, number | null>>)
    : model.library_price
      ? { input_mtok: model.library_price.input_mtok, output_mtok: model.library_price.output_mtok }
      : {};
  const shownRates = fields.filter((f) => current[f] != null || f !== "batch_factor");
  const historyRates = ALL_RATES.filter((f) => fields.includes(f) || (history?.prices ?? []).some((e) => e[f] != null));

  const historyColumns = [
    col.text<PriceEntry>("from", t("admin.models.prices.short.from"), (e) => e.effective_from, { width: 112 }),
    ...historyRates.map((f) => col.text<PriceEntry>(f, t(`admin.models.prices.short.${f}`), (e) => rateText(f, e[f]))),
    ...(perUnit || (history?.prices ?? []).some((e) => e.units)
      ? [col.text<PriceEntry>("units", t("admin.models.prices.short.units"), (e) => unitsText(e.units))]
      : []),
    ...(canManage
      ? [col.actions<PriceEntry>("", (e) => [{
          label: t("admin.models.prices.removeEntry"), icon: <IconTrash size={16} />, color: "red",
          onClick: () => removeEntry(e),
        }])]
      : []),
  ];

  return (
    <Modal
      opened
      onClose={onClose}
      size={1080}
      padding="xl"
      portalProps={{ target: "#admin-portal-root" }}
      title={
        <Stack gap={spacePx[1]}>
          <Title order={4}>{t("admin.models.prices.title")}</Title>
          <Group gap="xs">
            <Text size="sm" c="dimmed">{providerLabel ?? model.provider_key}</Text>
            <Text size="sm" ff="monospace">{model.model_id}</Text>
            <Badge size="sm" variant="light">{t(`admin.models.capability.${model.capability}`, { defaultValue: model.capability })}</Badge>
          </Group>
        </Stack>
      }
    >
      <Stack gap="lg">
        {error && <Alert color="red">{error}</Alert>}

        {/* What it is charged at now. */}
        <Stack gap="sm">
          <Group justify="space-between" align="center">
            <Text fw={600}>{t("admin.models.prices.current")}</Text>
            <Badge variant="light" color={SOURCE_COLOR[model.price]}>
              {model.price === "admin" && model.admin_price
                ? t("admin.models.prices.sourceAdmin", { date: model.admin_price.effective_from })
                : t(`admin.models.price.${model.price}`)}
            </Badge>
          </Group>
          {perUnit && model.admin_price?.units ? (
            <Text fw={700}>{unitsText(model.admin_price.units)}</Text>
          ) : model.price === "none" ? (
            <Alert color="orange">{t("admin.models.prices.noneHint")}</Alert>
          ) : (
            <SimpleGrid cols={{ base: 2, sm: Math.min(Math.max(shownRates.length, 1), 4) }}>
              {shownRates.map((field) => (
                <Card key={field}>
                  <Stack gap={spacePx[1]}>
                    <Text size="sm" c="dimmed">{t(`admin.models.prices.fields.${field}`)}</Text>
                    <Text fw={700} fz="xl">{rateText(field, current[field])}</Text>
                  </Stack>
                </Card>
              ))}
            </SimpleGrid>
          )}
          {model.price === "library" && model.library_price && (
            <Text size="xs" c="dimmed">
              {t("admin.models.prices.libraryNote", {
                model: model.library_price.matched_model, provider: model.library_price.provider_id,
              })}
              {model.library_price.tiered ? ` ${t("admin.models.prices.tieredNote")}` : ""}
            </Text>
          )}
        </Stack>

        <Divider />

        <SimpleGrid cols={{ base: 1, md: canManage ? 2 : 1 }} spacing="xl">
          {/* Admin price history. */}
          <Stack gap="sm">
            <Group justify="space-between" align="center">
              <Text fw={600}>{t("admin.models.prices.history")}</Text>
              {canManage && history && (
                <Button size="xs" variant="subtle" color="red" onClick={() => setConfirmDelete(true)}>
                  {t("admin.models.prices.removeAll")}
                </Button>
              )}
            </Group>
            {loading ? (
              <Skeleton h={88} />
            ) : history?.prices.length ? (
              <>
                <AdminTable columns={historyColumns} data={history.prices} getRowKey={(e) => e.effective_from} />
                <Text size="xs" c="dimmed">{t("admin.models.prices.units")}</Text>
              </>
            ) : (
              <Text size="sm" c="dimmed">
                {t(model.price === "library" ? "admin.models.prices.noAdminPriceLibrary" : "admin.models.prices.noAdminPrice")}
              </Text>
            )}
          </Stack>

          {/* Add an admin price entry. */}
          {canManage && (
            <Stack gap="sm">
              <Text fw={600}>{t("admin.models.prices.addEntry")}</Text>
              <DatePickerInput
                label={t("admin.models.prices.effectiveFrom")}
                value={from}
                onChange={setFrom}
                required
              />
              {perUnit && (
                <SimpleGrid cols={2}>
                  <TextInput
                    label={t("admin.models.prices.fields.unit")}
                    description={t("admin.models.prices.unitHint")}
                    placeholder="characters"
                    value={unit}
                    onChange={(e) => setUnit(e.currentTarget.value)}
                    error={unitInvalid ? t("admin.models.prices.unitInvalid") : undefined}
                  />
                  <NumberInput
                    label={t("admin.models.prices.fields.unitRate")}
                    value={unitRate}
                    onChange={setUnitRate}
                    min={0}
                    decimalScale={8}
                    allowNegative={false}
                  />
                </SimpleGrid>
              )}
              <SimpleGrid cols={2}>
                {fields.map((field) => (
                  <NumberInput
                    key={field}
                    label={t(`admin.models.prices.fields.${field}`)}
                    placeholder={current[field] != null ? rateText(field, current[field]) : undefined}
                    value={rates[field] ?? ""}
                    onChange={(value) => setRates((r) => ({ ...r, [field]: value }))}
                    min={field === "batch_factor" ? 0.01 : 0}
                    max={field === "batch_factor" ? 1 : undefined}
                    decimalScale={6}
                    allowNegative={false}
                  />
                ))}
              </SimpleGrid>
              <Text size="xs" c="dimmed">{t("admin.models.prices.propagation")}</Text>
              <Group justify="flex-end">
                <Button onClick={addEntry} loading={saving} disabled={!from || (filled.length === 0 && !unitFilled)}>
                  {t("admin.models.prices.save")}
                </Button>
              </Group>
            </Stack>
          )}
        </SimpleGrid>
      </Stack>

      <ConfirmModal
        opened={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => {
          setConfirmDelete(false);
          void change(async () => {
            await providerCatalogApi().deletePrice(model.provider_key, model.model_id);
            return null;
          });
        }}
        title={t("admin.models.prices.removeAllTitle")}
        message={t("admin.models.prices.removeAllImpact")}
        confirmLabel={t("admin.models.prices.removeAll")}
        cancelLabel={t("common.cancel")}
        portalProps={{ target: "#admin-portal-root" }}
        danger
      />
    </Modal>
  );
};

export default PriceModal;
